import type { TaskPriority } from './tasks';

/**
 * Task sources (T-15): upstream boards a project mirrors tasks from (GitHub Issues, Asana, Trello,
 * jflo). README §3.3 and design decisions 8 and 10: upstream owns the title and description; the
 * factory writes back state moves, the run's summary, the PR link and comments people choose to post.
 */

export const TASK_SOURCE_KINDS = ['github', 'asana', 'trello', 'jflo'] as const;
export type TaskSourceKind = (typeof TASK_SOURCE_KINDS)[number];

export function isTaskSourceKind(value: unknown): value is TaskSourceKind {
  return typeof value === 'string' && (TASK_SOURCE_KINDS as readonly string[]).includes(value);
}

export const TASK_SOURCE_LABELS: Record<TaskSourceKind, string> = {
  github: 'GitHub',
  asana: 'Asana',
  trello: 'Trello',
  jflo: 'jflo',
};

/**
 * Where the tasks are upstream. Each kind uses its own fields:
 * - github: `repo` ("owner/name");
 * - asana: `project` (the project gid from its URL);
 * - trello: `board` (the board id, or the short id from its URL);
 * - jflo: `url` (its API v1 root, e.g. https://jflo.app/api/v1) and optionally `type` (a task type,
 *   so one jflo board can feed several projects).
 */
export interface TaskSourceConfig {
  repo?: string;
  project?: string;
  board?: string;
  url?: string;
  type?: string;
  /**
   * SF-T-151, Asana and Trello: the single-choice custom field (an Asana enum field, a Trello dropdown)
   * a task's state is read from and moves write, instead of its section or list. Its options are then
   * the states the mapping maps, and sections and lists are left as people arrange them. Absent: the
   * section (Asana) or list (Trello).
   */
  status_field?: string;
  /**
   * SF-T-151, Asana and Trello: the single-choice custom field a task's priority is read from, whose
   * options `priority_map` maps. Absent: priority comes from the task's tags (Asana) or labels (Trello).
   * GitHub always reads labels.
   */
  priority_field?: string;
  /**
   * SF-T-151: which upstream values (options of `priority_field`, or labels and tags) mean which
   * priority here. Empty or absent: priority isn't synced, and people set it here. Otherwise it's
   * upstream's, read-only here: a task with none of these values has none.
   */
  priority_map?: TaskSourcePriorityMapping[];
}

/** SF-T-151: an upstream value (a field option, a label or tag) and the priority it means here. */
export interface TaskSourcePriorityMapping {
  upstream: string;
  priority: TaskPriority;
}

/** SF-T-151: the sources whose state can come from a custom field instead of a section or list. */
export const TASK_SOURCE_STATUS_FIELD_KINDS: readonly TaskSourceKind[] = ['asana', 'trello'];
/** SF-T-151: the sources priority can come in from (GitHub by labels only). */
export const TASK_SOURCE_PRIORITY_KINDS: readonly TaskSourceKind[] = ['asana', 'trello', 'github'];
/** SF-T-151: the sources whose priority can come from a custom field rather than labels or tags. */
export const TASK_SOURCE_PRIORITY_FIELD_KINDS: readonly TaskSourceKind[] = ['asana', 'trello'];

/** SF-T-151: whether a source's tasks take their priority from it (it maps some). */
export function syncsPriority(config: Pick<TaskSourceConfig, 'priority_map'> | null | undefined): boolean {
  return Boolean(config?.priority_map?.length);
}

/**
 * Which upstream tasks come in. Empty lists let everything through.
 * - `states`: upstream states (Asana sections, Trello lists, jflo columns, GitHub status labels or
 *   open/closed) a task must be in to be brought in.
 * - `labels`: it needs at least one of these (Asana tags, Trello labels, GitHub labels, jflo labels
 *   or #tags).
 * A task already mirrored keeps syncing when it moves out of the filter, so moves to Done still land.
 */
export interface TaskSourceFilters {
  states: string[];
  labels: string[];
}

/**
 * Coming in (SF-T-186): one upstream state and the column it lands in here. Several states can share a
 * column. `column_id: null` is a state explicitly not brought in, saved so Connect doesn't suggest a
 * column for it again; it's read as unmapped. Which state a move here goes upstream to is the outgoing
 * side's (TaskSourceOutgoing), never worked out from this.
 */
export interface TaskSourceMapping {
  upstream: string;
  column_id: string | null;
}

/**
 * SF-T-85: something done upstream when a task moves to a column here, after the outgoing state (which
 * puts it in a section, list or status label):
 * - close: completes it in Asana, closes the issue on GitHub, archives the card on Trello (ticks a checklist item);
 * - reopen: the opposite (also done on its own when a task the factory completed leaves for an open column
 *   whose outgoing side doesn't complete it);
 * - add_label / remove_label: an Asana tag, a Trello label or a GitHub label, by name (`value`);
 * - set_field: an Asana or Trello custom field, or a field of a GitHub Project the repo is linked to
 *   (`project`, its number; the issue is added to the project if it isn't in it), by name (`field`), to
 *   `value`: an option's name, or text or a number; empty clears it.
 * - move_section (SF-T-151, Asana and Trello): moves it to a section or list (`value`) even when its state
 *   comes from a custom field, so sections can follow along too.
 * SF-T-186: the old "Move to a status" (move_to) is the outgoing row's `state` now.
 */
export type TaskSourceActionKind = 'close' | 'reopen' | 'add_label' | 'remove_label' | 'set_field' | 'move_section';

export interface TaskSourceAction {
  kind: TaskSourceActionKind;
  /** The label, tag or field value. */
  value?: string;
  /** set_field: the custom field's name. */
  field?: string;
  /** set_field on GitHub: the number of the GitHub Project (v2) the field is in. */
  project?: number;
}

/**
 * Going out (SF-T-186): "When a task moves to <column>, move it to <state> upstream (none: leave it where
 * it is), then do <actions>", in order. Stored on its own, one row per column at most; a column without
 * one changes nothing upstream. Completing a task upstream is only ever this: the kind's completed state
 * as `state` (TASK_SOURCE_COMPLETED_STATE), or a close action. jflo takes the state only.
 */
export interface TaskSourceOutgoing {
  column_id: string;
  state: string | null;
  actions: TaskSourceAction[];
}

/**
 * SF-T-85's column rules, as task_source.column_rules still holds them: SF-T-186 moved them into
 * `outgoing` (its migration read them), and the column is dropped in a later release.
 */
export interface TaskSourceColumnRule {
  column_id: string;
  actions: Array<Omit<TaskSourceAction, 'kind'> & { kind: TaskSourceActionKind | 'move_to' }>;
}

/** The state that completes a task upstream when a move goes to it: Asana's "Completed", GitHub's "closed". */
export const TASK_SOURCE_COMPLETED_STATE: Partial<Record<TaskSourceKind, string>> = { asana: 'Completed', github: 'closed' };

const sameState = (a: string | null | undefined, b: string | null | undefined) => (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/** Whether a move to this column completes the task upstream: its state is the completed one, or it has a close action. */
export function outgoingCompletes(kind: TaskSourceKind, row: Pick<TaskSourceOutgoing, 'state' | 'actions'> | null | undefined): boolean {
  if (!row) return false;
  const completed = TASK_SOURCE_COMPLETED_STATE[kind];
  return Boolean(completed && row.state && sameState(row.state, completed)) || row.actions.some((a) => a.kind === 'close');
}

/**
 * The outgoing side a new source starts with (SF-T-186): each column goes out to the first state brought
 * into it, with no actions, so nothing completes unless someone can see it on that column's row (the
 * state is the completed one). Columns nothing comes into are left out: a move there changes nothing.
 */
export function defaultOutgoing(columns: Array<{ id: string }>, mapping: TaskSourceMapping[]): TaskSourceOutgoing[] {
  return columns.flatMap((c) => {
    const state = mapping.find((m) => m.column_id === c.id)?.upstream;
    return state ? [{ column_id: c.id, state, actions: [] }] : [];
  });
}

/** What a state is called on the kind's board: an Asana section, a Trello list, or a status (a status field's option, a GitHub status label, jflo's). */
export function taskSourceStateNoun(kind: TaskSourceKind, config: Pick<TaskSourceConfig, 'status_field'> = {}): string {
  if (config.status_field?.trim()) return 'status';
  return kind === 'asana' ? 'section' : kind === 'trello' ? 'list' : 'status';
}

/**
 * Where the two sides disagree (SF-T-186), one hint per column: a column whose state goes out as one
 * that comes back in to another column ("Done goes out as section “Review”; “Review” comes in as In
 * progress"). Allowed (a move the factory made isn't brought back by the next sync), only said.
 */
export function mappingDisagreements(
  kind: TaskSourceKind,
  config: Pick<TaskSourceConfig, 'status_field'>,
  columns: Array<{ id: string; name: string }>,
  mapping: TaskSourceMapping[],
  outgoing: TaskSourceOutgoing[]
): Array<{ column_id: string; hint: string }> {
  const noun = taskSourceStateNoun(kind, config);
  const out: Array<{ column_id: string; hint: string }> = [];
  for (const row of outgoing) {
    if (!row.state) continue;
    const column = columns.find((c) => c.id === row.column_id);
    const back = mapping.find((m) => sameState(m.upstream, row.state));
    const comesTo = back?.column_id ? columns.find((c) => c.id === back.column_id) : null;
    if (!column || !comesTo || comesTo.id === column.id) continue;
    out.push({ column_id: column.id, hint: `${column.name} goes out as ${noun} “${row.state}”; “${row.state}” comes in as ${comesTo.name}` });
  }
  return out;
}

/** Which actions each kind of source can take (jflo takes none: its own moves do it). */
export const TASK_SOURCE_ACTIONS: Record<TaskSourceKind, readonly TaskSourceActionKind[]> = {
  asana: ['close', 'reopen', 'move_section', 'add_label', 'remove_label', 'set_field'],
  trello: ['close', 'reopen', 'move_section', 'add_label', 'remove_label', 'set_field'],
  github: ['close', 'reopen', 'add_label', 'remove_label', 'set_field'],
  jflo: [],
};

/** What each action is called on the settings page, by kind. */
export function taskSourceActionLabel(kind: TaskSourceKind, action: TaskSourceActionKind): string {
  const label = kind === 'asana' ? 'tag' : 'label';
  switch (action) {
    case 'close':
      return kind === 'asana' ? 'Mark it complete' : kind === 'trello' ? 'Archive the card' : 'Close the issue';
    case 'reopen':
      return kind === 'asana' ? 'Mark it incomplete' : kind === 'trello' ? 'Unarchive the card' : 'Reopen the issue';
    case 'add_label':
      return `Add a ${label}`;
    case 'remove_label':
      return `Remove a ${label}`;
    case 'set_field':
      return kind === 'github' ? 'Set a project field' : 'Set a custom field';
    case 'move_section':
      return kind === 'trello' ? 'Move to a list' : 'Move to a section';
  }
}

/** T-58: someone on the source, by their account id there (GitHub user id, Asana gid, Trello member id, jflo person id) and name. */
export interface TaskSourcePerson {
  id: string;
  name: string;
}

/**
 * SF-T-101: someone who can be tagged in a comment on the source: their account id and name there, and
 * the handle a plain-text @mention names (a GitHub login, a Trello username, a jflo handle); null where
 * the tracker tags by id (Asana).
 */
export interface UpstreamPerson extends TaskSourcePerson {
  handle?: string | null;
}

/**
 * SF-T-101: who "Send to <source>" can tag on a mirrored task: the person who created it there (null
 * when the source can't tell), and the board's people to pick someone else from. `error` says why the
 * board's people (or the creator) couldn't be read; whatever was read still comes back.
 */
export interface TaskUpstreamPeople {
  creator: UpstreamPerson | null;
  people: UpstreamPerson[];
  error: string | null;
}

/** A source as the settings page shows it. The token never comes back, only whether one is set. */
export interface TaskSource {
  id: string;
  kind: TaskSourceKind;
  name: string;
  config: TaskSourceConfig;
  filters: TaskSourceFilters;
  /** Coming in: where each upstream state lands here (SF-T-186). */
  mapping: TaskSourceMapping[];
  /** Going out: what a move to each column does upstream (SF-T-186). */
  outgoing: TaskSourceOutgoing[];
  /** @deprecated SF-T-186: always empty, so a page opened before the deploy still opens a source (its save is refused: reload). */
  column_rules?: never[];
  enabled: boolean;
  /**
   * The scheduled check: the whole board read every TASK_SOURCE_SCHEDULED_MINUTES, on by default for
   * new sources (SF-T-85), as a back-up for the webhook.
   */
  scheduled_sync: boolean;
  /**
   * T-84: "Agents may post comments to <source>": `sf-agent tasks comment --also-post` posts there too, signed
   * "— AI Agent via Software Factory". Off by default: an agent's comment then stays in the factory, and the agent is told.
   */
  agents_may_post: boolean;
  /**
   * SF-T-83: agents working in a checkout of the project may read items its tasks link to on this source
   * (`sf-agent tasks source <url>`), read-only, with its token. Off by default.
   */
  agents_read: boolean;
  /**
   * T-58: people upstream whose replies may answer the AI builder's questions by themselves, by their
   * account id there (the name is only a label). Anyone else's answer is only pre-selected for a person
   * to send. Empty: nobody's.
   */
  trusted_authors: TaskSourcePerson[];
  has_token: boolean;
  /** First 12 hex characters of the token's HMAC, to tell two tokens apart. */
  token_fingerprint: string | null;
  last_synced_at: string | null;
  /** SF-T-85: the last read left tasks for later (more than one run reads, or a webhook's sync failed): the next cron run carries on. */
  catching_up: boolean;
  last_error: string | null;
  last_error_at: string | null;
  /** SF-T-175: the last move or rule the source left partly undone on purpose ("T-58: set_field: the sub-task doesn't hold …"), and when. */
  last_skip: string | null;
  last_skip_at: string | null;
  /**
   * SF-T-187: the board as last read (Connect, a save, Sync now, the scheduled check at most hourly), so
   * the dialog opens knowing its states, labels and fields; null until it's read once. Read without the
   * status field: `boardStates` (shared/task-source-board.ts) gives the states as the source maps them.
   */
  board: TaskSourceCheck | null;
  board_checked_at: string | null;
  /** SF-T-187: what the source's settings name that the stored board hasn't got ('section "Doing"'); empty: all there, or not read yet. */
  board_missing: string[];
  webhook: TaskSourceWebhook;
  /** Tasks mirrored from it. */
  task_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * How the source hears about changes between reconciles:
 * - off: no webhook (paused, or the factory has no public address): "Sync now", or the 10-minute check when it's on;
 * - registered: the factory set it up upstream itself (GitHub sources share the repo's webhook);
 * - manual: someone adds `url` upstream by hand (jflo's AI builder webhook);
 * - failed: setting it up didn't work (`error` says why); the reconcile carries on.
 */
export type TaskSourceWebhookStatus = 'off' | 'registered' | 'manual' | 'failed';

export interface TaskSourceWebhook {
  status: TaskSourceWebhookStatus;
  /** Its address, for adding by hand (jflo). Its token is the only secret in it. */
  url: string | null;
  error: string | null;
  /** When the last delivery came. */
  last_at: string | null;
  /** A signing secret is set (Asana's handshake, or one pasted for jflo or Trello), so deliveries without its signature are refused. */
  has_secret: boolean;
}

/**
 * The credentials, sent only when setting or replacing them. Trello needs its API key as well as the
 * token; the others need only `token` (a GitHub token, an Asana personal access token, a jflo API key).
 */
export interface TaskSourceCredentials {
  token: string;
  key?: string;
}

export interface TaskSourceInput {
  kind?: TaskSourceKind;
  name?: string;
  config?: TaskSourceConfig;
  filters?: Partial<TaskSourceFilters>;
  mapping?: TaskSourceMapping[];
  /** Left out on create: defaultOutgoing (each column to the first state brought into it). */
  outgoing?: TaskSourceOutgoing[];
  enabled?: boolean;
  scheduled_sync?: boolean;
  agents_may_post?: boolean;
  agents_read?: boolean;
  trusted_authors?: TaskSourcePerson[];
  credentials?: TaskSourceCredentials;
  /** PATCH: the source's updated_at when the dialog opened; a save over a newer change is refused (409 TASK_SOURCE_STALE). */
  expected_updated_at?: string;
  /**
   * jflo: the AI_BUILDER_WEBHOOK_SECRET jflo signs its webhook with; Trello (SF-T-85): the API key's
   * secret, which Trello signs deliveries with. Write-only; "" clears it.
   */
  webhook_secret?: string;
}

/**
 * POST /api/project/sources/import: a source drafted from a checkout's devkit.config.json (its
 * `tasks` block, as the CLI's `pnpm tasks` reads it). `text` is the file pasted in; left out, the
 * factory reads it from the project's repo on GitHub. The token is never in the file.
 */
export interface TaskSourceImportInput {
  text?: string;
}

export interface TaskSourceImport {
  draft: Required<Pick<TaskSourceInput, 'kind' | 'name' | 'config' | 'mapping'>> & { filters: TaskSourceFilters };
  /** What to check or fill in by hand: a state with no column, the token, a default URL. */
  notes: string[];
}

/**
 * POST /api/project/sources/check: connects with these settings (or the saved source's token when
 * `source_id` is given and no credentials are) and says what's there, for the mapping and filters.
 */
export interface TaskSourceCheckInput {
  kind: TaskSourceKind;
  config: TaskSourceConfig;
  credentials?: TaskSourceCredentials;
  source_id?: string;
}

export interface TaskSourceCheck {
  /** Whose token it is: comments the factory posts upstream appear as them. */
  me: { id: string; name: string };
  /** The upstream states, in board order. */
  states: string[];
  /** The labels (or tags) in use, for the filter. */
  labels: string[];
  /**
   * SF-T-85: the custom fields a column rule can set, with their options where they have a list; on
   * GitHub, the fields of the Projects the repo is linked to, each with its project. Absent: none (jflo).
   */
  fields?: TaskSourceCheckField[];
  /**
   * SF-T-151, Asana and Trello: the project's sections or the board's lists, in board order, for a rule
   * that moves a task to one (`move_section`). The same as `states` unless a status field is set.
   */
  sections?: string[];
}

/**
 * SF-T-187: the board as stored on the source (`task_source.board`): what check() reads, plus the board's
 * people (members()), for who can be tagged and trusted. Names, ids and options only, never the token.
 */
export interface TaskSourceBoard extends TaskSourceCheck {
  /** Absent: the source can't list them, or they couldn't be read. */
  people?: UpstreamPerson[];
}

export interface TaskSourceCheckField {
  name: string;
  options: string[];
  /**
   * SF-T-151: a single-choice field (an Asana enum, a Trello dropdown): one that can hold a task's
   * status or priority (`status_field`, `priority_field`).
   */
  choice?: boolean;
  /** GitHub: the Project (v2) it's a field of. */
  project?: { number: number; title: string };
}

/** What one reconcile did. */
export interface TaskSourceSyncResult {
  created: number;
  updated: number;
  moved: number;
  comments: number;
  archived: number;
  /** Files copied in from upstream. */
  files: number;
  /** Tasks left for the next run (each run reads at most so many details). */
  deferred: number;
  errors: string[];
}

/**
 * SF-T-76: a person or task a mirrored task's text names on its source (shared/source-refs.ts), as the
 * sync read it there and keeps it (task_source_ref). Only ones the source has: anything it doesn't (not
 * on the board, gone) shows as written.
 */
export interface TaskSourceRef {
  type: 'person' | 'task';
  key: string;
  /** The person's name or the task's title there. */
  name: string;
  url: string | null;
  /** A task's short name there ("#12", "T-424"); '' when it has none (Asana) and for people. */
  ref: string;
  /** A task also mirrored in this project: it links here instead. */
  task: { id: string; number: number; title: string } | null;
}

/** SF-T-76: what a mirrored task's text names on its source, and what the text is read against. */
export interface TaskSourceRefs {
  kind: TaskSourceKind;
  /** GitHub: the source's repo, for `#12` and issue links. */
  repo: string | null;
  /** jflo: the source's API root, for task links on its host. */
  url: string | null;
  refs: TaskSourceRef[];
}

/** Where a mirrored task lives upstream: the badge on its card and its page. */
export interface TaskUpstream {
  source_id: string;
  kind: TaskSourceKind;
  source_name: string;
  /** How upstream names it: "#57", "T-431", a Trello card number, or "" for Asana. */
  ref: string;
  url: string | null;
  /** Its upstream state when last read. */
  state: string | null;
  /** The source is on and the task is still there (not gone upstream): T-58, only then can questions be sent there. */
  enabled: boolean;
  /** SF-T-151: its priority comes from the source (it maps some), so it's read-only here. */
  priority_synced: boolean;
  /**
   * SF-T-151: a broken link. The source refused the task to the source's token (403) when it was last
   * looked up or written to: since when, and the source's reason. Cleared once it's read again.
   */
  unreachable?: { since: string; reason: string } | null;
}

/** "GitHub #57", "jflo T-431", "Asana". */
export function upstreamBadgeText(upstream: Pick<TaskUpstream, 'kind' | 'ref'>): string {
  const label = TASK_SOURCE_LABELS[upstream.kind];
  return upstream.ref ? `${label} ${upstream.ref}` : label;
}

/**
 * The reconcile cron (wrangler.toml [triggers]): every 10 minutes. It reads the sources with the
 * scheduled check on (`scheduled_sync`, on by default since SF-T-85) once their last full read is
 * TASK_SOURCE_SCHEDULED_MINUTES old, and any source with work left over (a cut-short sync, a webhook
 * that failed) straight away. The other cron is the daily task hub wake.
 */
export const TASK_SOURCE_SYNC_CRON = '*/10 * * * *';

/** SF-T-85: how often the scheduled check reads the whole board. A back-up for the webhook, so not real time. */
export const TASK_SOURCE_SCHEDULED_MINUTES = 30;
